import { useState, useEffect, useCallback } from 'react';
import { getLocationTreeBySite, createLocation } from '../api';

function TreeNode({ node, depth, onSelect }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className="location-tree-node"
        style={{ paddingLeft: depth * 20 + 12 }}
        onClick={() => onSelect(node)}
      >
        {hasChildren && (
          <span
            className="location-tree-toggle"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? '\u25BC' : '\u25B6'}
          </span>
        )}
        {!hasChildren && <span className="location-tree-toggle" style={{ visibility: 'hidden' }}>{'\u25B6'}</span>}
        <span className="location-tree-name">{node.name}</span>
        {node.floorNumber != null && <span className="location-tree-type">{node.floorNumber < 0 ? `B${Math.abs(node.floorNumber)}` : node.floorNumber === 0 ? 'G' : `F${node.floorNumber}`}</span>}
        {node.locationType && <span className="location-tree-type">{node.locationType}</span>}
      </div>
      {hasChildren && expanded && node.children.map((child) => (
        <TreeNode key={child.id} node={child} depth={depth + 1} onSelect={onSelect} />
      ))}
    </div>
  );
}

function flattenNodes(nodes) {
  const result = [];
  const walk = (list) => {
    for (const n of list) {
      result.push({ id: n.id, name: n.name });
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return result;
}

function buildPathMap(nodes, parentPath = '') {
  const map = {};
  for (const n of nodes) {
    const path = parentPath ? `${parentPath} > ${n.name}` : n.name;
    map[n.id] = path;
    if (n.children) {
      Object.assign(map, buildPathMap(n.children, path));
    }
  }
  return map;
}

export default function LocationPickerModal({ isOpen, onClose, onSelect, siteId, organisationId, title = 'Pick Location' }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addParentId, setAddParentId] = useState('');
  const [addType, setAddType] = useState('');
  const [addFloor, setAddFloor] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const loadTree = useCallback(() => {
    if (!siteId) return;
    setLoading(true);
    getLocationTreeBySite(siteId)
      .then((data) => setTree(data))
      .catch(() => setTree([]))
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => {
    if (isOpen) {
      loadTree();
      setShowAdd(false);
      setAddName('');
      setAddParentId('');
      setAddType('');
      setAddFloor('');
      setError('');
    }
  }, [isOpen, loadTree]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [isOpen]);

  const pathMap = buildPathMap(tree);

  const handleSelect = (node) => {
    onSelect({ id: node.id, name: node.name, path: pathMap[node.id] || node.name, floorNumber: node.floorNumber });
    onClose();
  };

  const handleAdd = async () => {
    if (!addName.trim()) return;
    if (!siteId) { setError('No site linked to this report'); return; }
    setAdding(true);
    setError('');
    try {
      await createLocation({
        name: addName.trim(),
        organisationId: organisationId ? Number(organisationId) : null,
        siteId: siteId ? Number(siteId) : null,
        parentId: addParentId ? Number(addParentId) : null,
        locationType: addType || null,
        floorNumber: addFloor !== '' ? Number(addFloor) : null,
      });
      setAddName('');
      setAddParentId('');
      setAddType('');
      setAddFloor('');
      setShowAdd(false);
      loadTree();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  const allNodes = flattenNodes(tree);

  return (
    <div className="location-modal-overlay" onClick={onClose}>
      <div className="location-modal" onClick={(e) => e.stopPropagation()}>
        <div className="location-modal-header">
          <h3>{title}</h3>
          <button className="location-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="location-modal-body">
          {loading && <p style={{ padding: '1rem', color: '#888' }}>Loading locations...</p>}
          {!loading && tree.length === 0 && <p style={{ padding: '1rem', color: '#888' }}>No locations found.</p>}
          {!loading && tree.map((node) => (
            <TreeNode key={node.id} node={node} depth={0} onSelect={handleSelect} />
          ))}
        </div>

        <div className="location-add-section">
          {!showAdd ? (
            <button className="btn-link" onClick={() => setShowAdd(true)} style={{ padding: '0.5rem 0' }}>+ Add Location</button>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input
                  placeholder="Location name"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  style={{ flex: 2, minWidth: 120, padding: '0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                />
                <select
                  value={addParentId}
                  onChange={(e) => setAddParentId(e.target.value)}
                  style={{ flex: 1, minWidth: 100, padding: '0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem' }}
                >
                  <option value="">No parent</option>
                  {allNodes.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
                <input
                  placeholder="Type (optional)"
                  value={addType}
                  onChange={(e) => setAddType(e.target.value)}
                  style={{ flex: 1, minWidth: 80, padding: '0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem' }}
                />
                <select
                  value={addFloor}
                  onChange={(e) => setAddFloor(e.target.value)}
                  style={{ flex: 0, minWidth: 90, padding: '0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem' }}
                >
                  <option value="">Floor</option>
                  {Array.from({ length: 12 }, (_, i) => i - 1).map((f) => (
                    <option key={f} value={f}>{f < 0 ? `B${Math.abs(f)}` : f === 0 ? 'Ground' : `Floor ${f}`}</option>
                  ))}
                </select>
              </div>
              {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.3rem 0' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }} onClick={handleAdd} disabled={adding}>
                  {adding ? 'Adding...' : 'Add'}
                </button>
                <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', color: '#333', borderColor: '#ccc' }} onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
